import * as React from "react";
import { Plus, SlidersHorizontal, X } from "lucide-react";
import { cn } from "@/modern/utils";
import {
  ENTITY_FINDER_FOOTER_CHIP_CLS,
  ENTITY_FINDER_FOOTER_CLS,
} from "@/modern/components/entity-finder/constants/layout";
import { ENTITY_FINDER_SCOPE_ORDER, getEntityFinderScopeMeta } from "@/modern/components/entity-finder/constants/config";
import type { EntityFinderKind } from "@/modern/components/entity-finder/types";
import { renderEntityFinderConfigIcon } from "@/modern/components/entity-finder/components/renderEntityFinderConfigIcon";

interface EntityFinderScopeChipsProps {
  scopes: EntityFinderKind[];
  onAdd: (scope: EntityFinderKind) => void;
  onRemove: (scope: EntityFinderKind) => void;
  onClearAll: () => void;
  className?: string;
}

function ScopeAdder({
  options,
  onAdd,
  compact = false,
}: {
  options: EntityFinderKind[];
  onAdd: (scope: EntityFinderKind) => void;
  compact?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [menuMaxHeight, setMenuMaxHeight] = React.useState<number>();
  const ref = React.useRef<HTMLDivElement>(null);

  const updateMenuMaxHeight = React.useCallback(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const margin = 12;
    const available = window.innerHeight - rect.bottom - margin;
    setMenuMaxHeight(Math.max(100, available));
  }, []);

  React.useLayoutEffect(() => {
    if (!open) {
      setMenuMaxHeight(undefined);
      return;
    }
    updateMenuMaxHeight();
    window.addEventListener("resize", updateMenuMaxHeight);
    window.addEventListener("scroll", updateMenuMaxHeight, true);
    return () => {
      window.removeEventListener("resize", updateMenuMaxHeight);
      window.removeEventListener("scroll", updateMenuMaxHeight, true);
    };
  }, [open, updateMenuMaxHeight]);

  React.useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  React.useEffect(() => {
    if (open && options.length === 0) setOpen(false);
  }, [open, options.length]);

  if (options.length === 0) return null;

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "inline-flex items-center gap-1 rounded-full border border-dashed border-border/80 text-muted-foreground transition-colors hover:border-border hover:bg-accent hover:text-foreground",
          compact ? "h-7 px-2 text-xs" : "px-2.5 py-1 text-sm bg-muted"
        )}
        aria-label="Add search filter"
        aria-expanded={open}
      >
        <Plus size={compact ? 11 : 12} />
        {compact ? "Filter" : "Add filter"}
      </button>
      {open && (
        <div
          className={cn(
            "absolute left-0 top-full z-20 mt-1 w-44 overflow-y-auto overscroll-contain rounded-md border border-border bg-popover p-1 shadow-lg",
          )}
          style={menuMaxHeight != null ? { maxHeight: menuMaxHeight } : undefined}
        >
          {options.map((scope) => {
            const meta = getEntityFinderScopeMeta(scope);
            return (
              <button
                key={scope}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onAdd(scope)}
                className={cn("flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm text-foreground hover:bg-accent")}
              >
                {renderEntityFinderConfigIcon(meta.icon, 12, "text-xs text-muted-foreground")}
                {meta.plural}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function EntityFinderScopeChips({
  scopes,
  onAdd,
  onRemove,
  onClearAll,
  className,
}: EntityFinderScopeChipsProps) {
  const addable = ENTITY_FINDER_SCOPE_ORDER.filter((scope) => !scopes.includes(scope));

  return (
    <div
      className={cn(
        "flex min-h-9 items-center gap-2 border-t border-border/60 px-3 py-1.5",
        className
      )}
    >
      <SlidersHorizontal size={12} className="shrink-0 text-muted-foreground/70" aria-hidden />
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
        {scopes.map((scope) => {
          const meta = getEntityFinderScopeMeta(scope);
          return (
            <button
              key={scope}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onRemove(scope)}
              aria-label={`Remove ${meta.label} filter`}
              className={cn(
                "inline-flex h-7 max-w-full items-center gap-1 rounded-full border border-border/70 bg-secondary/80 px-2 transition-colors hover:bg-accent hover:text-foreground",
                ENTITY_FINDER_FOOTER_CHIP_CLS
              )}
            >
              {renderEntityFinderConfigIcon(meta.icon, 10, "text-[10px] shrink-0")}
              <span className="truncate">{meta.plural}</span>
              <X size={10} className="shrink-0 text-muted-foreground" aria-hidden />
            </button>
          );
        })}
        {scopes.length === 0 && (
          <span className={ENTITY_FINDER_FOOTER_CLS}>All types</span>
        )}
        <ScopeAdder options={addable} onAdd={onAdd} compact />
      </div>
      {scopes.length > 0 && (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onClearAll}
          className={cn("shrink-0 transition-colors hover:text-foreground", ENTITY_FINDER_FOOTER_CLS)}
        >
          Clear all
        </button>
      )}
    </div>
  );
}
